<script setup lang="ts" async>
import type {Faq} from '@/types/faqs';
import {useChangeCase} from '@vueuse/integrations/useChangeCase';

useMenuState().value = 'collapsed';

const currentIndex = ref(0);
const faqs = ref<Faq[]>([]);
faqs.value = await $fetch(`/api/faqs`);
</script>

<template>
  <NuxtLayout name="text-only">
    <section class="faq-page w-full flex flex-col gap-[var(--standard-page-col-gap)]">
      <h1 class="uppercase font-bold text-[8.8rem]">{{ $t('faqs') }}</h1>

      <div class="flex flex-col gap-[2rem]">
        <h2 class="text-[4.9rem] font-bold">Frequently Asked Questions</h2>
        <p class="text-[2.2rem]">Here are some of the most common questions we receive. If you have a question that is not answered here, please feel free to contact us.</p>
      </div>

      <Divider />

      <div class="flex flex-col gap-[4rem]">
        <div class="faq-menu flex w-full gap-[1.5rem]" role="tablist">
          <template v-for="(faq, index) in faqs" :key="faq.title">
            <NuxtLink :id="faq.title" class="faq-link rounded-full text-[1.8rem] text-center font-bold cursor-pointer py-[1rem] pb-[.8rem]" :style="`background-color: var(${faq.color}); border: 2px solid ${faq.color === '--general-color' ? 'black' : faq.color}; color: ${faq.color === '--general-color' ? 'black' : 'white'}`" role="tab" :aria-selected="currentIndex === index" :aria-controls="faq.title + 'Faqs'" :to="`#${useChangeCase(faq.title, 'camelCase').value}`">{{ faq.title }}</NuxtLink>
          </template>
        </div>
        :

        <div class="flex flex-col gap-[6rem]">
          <div v-for="faq in faqs" :id="`${useChangeCase(faq.title, 'camelCase').value}`" :key="faq.title" role="tabpanel" :aria-labelledby="faq.title" class="flex flex-wrap gap-8 mb-4 faq-section">
            <h2 class="text-[4.9rem] font-bold">{{ faq.title }}</h2>
            <div class="w-full">
              <Accordion v-if="faq.questions" :items="faq.questions" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </NuxtLayout>
</template>

<style lang="scss" scoped>
@use '@/assets/scss/queries';
.faq-menu {
  flex-wrap: wrap;
  .faq-link {
    flex: 1;

    @include queries.for-size(tablet-md) {
      flex: 25%;
    }

    @include queries.for-size(phone) {
      flex: 45%;
    }
  }
}
.faq-page {
  .faq-section {
    scroll-margin-top: var(--header-desktop-collapse-height);

    @include queries.for-size(phone) {
      scroll-margin-top: var(--header-mobile-collapse-height);
    }
  }
}
</style>
